import { FullScreenLoading } from '@bfa/common-ui';
import { externalRedirect } from '@bfa/nextjs-common/utils';
import { useRouter } from 'next/router';
import React, { useEffect, useRef } from 'react';

import Layout from '../../../components/Layout';
import { ABSOLUTE_URLS } from '../../../constants';
import { useGwsEligibilityCheck } from '../../../graphql/hooks/useGwsEligibilityCheck';
import { logAnalyticsEvent } from '../../../utils/analytics';
import { getGqlProgram } from '../../../utils/getGqlProgram';
import Popup from '../components/Popup';
import useGetAlert from './hooks/useGetAlert';
import {
  getAnnualUpgradeEventData,
  getUrlWithParams,
  getValidAnnualUpgradeGwsType,
  handleLogAndRedirection,
} from './utils/annualUpgrade';

type Props = {
  cross?: boolean;
  dismissUrl?: string;
  flowType: string;
};

const ALERT_ID = 'annual-upgrade-';

const UpgradePage = ({ flowType, dismissUrl, cross }: Props) => {
  const trackSent = useRef<boolean>(false);

  const subProgram = useRouter().query?.program?.toString() ?? '';

  const gqlProgram = getGqlProgram(subProgram);

  const gwsType = getValidAnnualUpgradeGwsType(gqlProgram);

  const { loading: gwsLoading, ...gwsResponse } = useGwsEligibilityCheck(
    gwsType,
    {
      program: gqlProgram,
    }
  );

  const shouldSkip = !!gwsResponse.error;

  const { data, loading, error } = useGetAlert({
    alertId: ALERT_ID.concat(subProgram.toLowerCase()),
    shouldSkip,
  });
  const alert = data?.me?.alert;
  const confirmQueryParms: Record<string, any> | string = {
    flowType: flowType,
    program: subProgram,
  };

  const trackLogo = React.useCallback(() => {
    logAnalyticsEvent(
      getAnnualUpgradeEventData(
        'Clicked IPSY logo on Annual Upgrade Interstitial'
      )
    );
  }, []);

  const trackDismiss = React.useCallback(() => {
    logAnalyticsEvent(
      getAnnualUpgradeEventData(
        'Clicked Dismiss on Annual Upgrade Interstitial'
      )
    );
  }, []);

  const trackConfirm = React.useCallback(() => {
    logAnalyticsEvent(
      getAnnualUpgradeEventData(
        'Clicked Upgrade on Annual Upgrade Interstitial'
      )
    );
  }, []);

  const trackMaybe = React.useCallback(() => {
    logAnalyticsEvent(
      getAnnualUpgradeEventData(
        'Clicked No Thanks on Annual Upgrade Interstitial'
      )
    );
  }, []);

  useEffect(() => {
    if (!gwsLoading) {
      handleLogAndRedirection(
        !shouldSkip && !loading && (!alert || !!error),
        trackSent,
        gwsResponse.error
      );
    }
  }, [gwsResponse.error, gwsLoading, shouldSkip, loading, alert, error]);

  if (loading || !alert || error) {
    return <FullScreenLoading loading spinnerWidth={4.4} />;
  }

  const confirmUrlWithParams = getUrlWithParams(
    alert.confirmUrl,
    confirmQueryParms
  );

  return (
    <Layout
      header={{
        minimum: true,
        cross,
        onCrossClick: () => {
          trackDismiss();
          externalRedirect(ABSOLUTE_URLS.GLAM_BAG);
        },
        onLogoClick: () => {
          trackLogo();
        },
      }}
      footer={false}
    >
      <Popup
        {...alert}
        confirmUrl={confirmUrlWithParams}
        primaryActionOnClick={() => {
          trackConfirm();
        }}
        secondaryActionOnClick={() => {
          trackMaybe();
        }}
        dismissUrl={dismissUrl}
      />
    </Layout>
  );
};

export default UpgradePage;
